// The per-field Vuetify validation-rule builder, extracted verbatim from the
// `getFieldValidations` method of
// components/defaultLayout/EntityDialog&Forms/EntityMaskForm.vue. Pulled into a
// pure module so the (fiddly, branch-heavy) rule assembly is unit-tested and
// survives the Nuxt 2 -> Nuxt 3 migration; the component now forwards to this.
// Component state read off `this` (inputFields, entityParentObjectForCreate) is
// passed in explicitly. Inline console.debug noise from the original is dropped.
//
// Returns the array of Vuetify rule functions (each returns `true` or an error
// string), or undefined when the field needs no validation.

import type {
  IEntityMaskData,
  IEntityParentObjectForCreate,
} from '~/types/models'
import { assertIsNotUndefined } from '~/types/models'
import {
  validateNumericField,
  isNumericFieldType,
} from '~/cynqueModules/dataObjects/Numeric'
import {
  validateKitQuantity,
  getMSServiceQuantityValidator,
} from '~/cynqueModules/dataObjects/KitQuantity'
import { validateTableHeaderWidth } from '~/cynqueModules/dataObjects/TableHeaderWidth'
import { validateIpAddress } from '~/cynqueModules/dataObjects/IpAddress'
import { validateSubnetMask } from '~/cynqueModules/dataObjects/SubnetMask'
import { validateUrl } from '~/cynqueModules/dataObjects/url'
import { validateLabelForFieldName } from '~/cynqueModules/dataObjects/LabelForFieldName'
import { validateOnePerSection } from '~/cynqueModules/onePerSectionSelect'
import { validateOptionsBuildContent } from '~/cynqueModules/kitOptionContent'
import { formatBytes } from '~/cynqueModules/formatBytes'
import { splitDelimitedString } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'
import { isBareEmailAddress } from '~/cynqueModules/emailRecipientRules'

// The single-address checks, in the order their messages should win. Shared by
// a plain email field (each is its own Vuetify rule, so the first failing
// message shows) and a delimited-list field (applied to every entry).
const EMAIL_ADDRESS_RULES: ((value: string) => true | string)[] = [
  (value) => value.indexOf('@') !== 0 || 'Email should have a username.',
  (value) => value.includes('@') || 'Email should include an @ symbol.',
  (value) =>
    value.lastIndexOf('.') - value.indexOf('@') > 1 ||
    'Email should contain a valid domain.',
  (value) => value.includes('.') || 'Email should include a period.',
  (value) =>
    value.lastIndexOf('.') <= value.length - 3 ||
    'Email should contain a valid domain extension.',
]

// One rule for a list of addresses: the first failing entry, named, so the
// user can see which tag to fix. An entry may be a picked item (e.g. a
// contact), checked by its `itemValue` property (its email).
function makeEveryEmailAddressRule(itemValue?: string) {
  return (value: unknown): true | string =>
    firstBadEmailAddress(splitDelimitedString(value, itemValue))
}
function firstBadEmailAddress(addresses: string[]): true | string {
  for (const address of addresses) {
    for (const rule of EMAIL_ADDRESS_RULES) {
      const result = rule(address)
      if (result !== true) {
        return `${address}: ${result}`
      }
    }
    // What the rules above let through but a mail server will not take: a
    // display name, a space, or a comma or semicolon that would make one entry
    // two recipients. Mirrors cynque-api-server's check (emailRecipientRules).
    if (!isBareEmailAddress(address)) {
      return `${address}: not a plain email address (name@domain).`
    }
  }
  return true
}

export function buildFieldValidations(
  ifield: IEntityMaskData,
  opts: {
    inputFields: IEntityMaskData[]
    entityParentObjectForCreate: IEntityParentObjectForCreate | undefined
    // The row being edited (undefined when creating). Read only by rules that
    // depend on which row it is -- see msServiceQuantity.
    entityData?: { parentMSServiceId?: unknown }
  }
): Function[] | undefined {
  const { inputFields, entityParentObjectForCreate, entityData } = opts
  // A field the user cannot change must not be able to block Save -- for
  // disabled fields, and equally for readonly ones (readonlyBasedOnState:
  // 'EDITING' replaced disabling on the email spec, and a stored value that
  // predates today's rules would otherwise make an existing email unsaveable).
  // Vuetify itself validates readonly inputs, hence the explicit skip.
  if (ifield.disabled || ifield.unrendered || ifield.readonly) {
    return undefined
  }
  const validationArray = [] as Function[]
  // Auto-apply a type-aware numeric rule to numeric fields so pasted bad data
  // (which bypasses the keypress gate) is flagged. Driven off the field type, so
  // it applies even to numeric fields that declare no `validation` (e.g. numEmployees).
  if (isNumericFieldType(ifield.type)) {
    validationArray.push((value: any) =>
      validateNumericField(value, ifield.type, ifield.label || ifield.name)
    )
  }
  if (!ifield.validation) {
    return validationArray.length ? validationArray : undefined
  }
  // A validation object can be made conditional: when doValidateIfFunction is
  // supplied and returns false, none of this object's declared rules apply.
  // (e.g. address fields are required only for domestic/US addresses.)
  if (
    ifield.validation.doValidateIfFunction &&
    !ifield.validation.doValidateIfFunction({
      inputFields,
      entityParentObjectForCreate,
    })
  ) {
    return validationArray.length ? validationArray : undefined
  }
  // An empty list is [], which `!!value` would call filled. That was handled
  // for delimited-list fields only, so EVERY `multiple` select passed
  // `required` once a user removed its last chip (Location(s), for one). Judge
  // by the VALUE, not the spec: `forceSingleSelectWhen` can turn a `multiple`
  // select single at runtime, and its value is then a lone object.
  const isListField = ifield.delimitedStringSeparator !== undefined
  if (ifield.validation.required) {
    validationArray.push(
      (value: any) =>
        (Array.isArray(value) ? value.length > 0 : !!value) ||
        `${ifield.label} can not be blank.`
    )
  }
  // ⚠ DELIBERATELY OUTSIDE the `if (ifield.value)` gate below, like `required`
  // and `onePerSection`.
  //
  // A rule inside that gate exists only when the field already holds a value, so
  // its presence depends on form state rather than on the spec. That coupling is
  // what produced the "an unrendered field's required rule never runs" class of
  // bug, and it would silently make this guard conditional on something
  // unrelated to file size. Out here the rule is a function of the spec alone.
  //
  // The rule no-ops on an empty value itself: `required` owns emptiness.
  //
  // On TIMING, so the next reader is not misled: EntityMaskForm empties
  // `fieldValidations` at dialog-open (clearValidationsArray) and builds it only
  // at the top of save(), so EVERY rule in this file -- `required` included --
  // is evaluated when the user clicks Save, not while they type or pick. That is
  // early enough for the purpose here: validation runs before the checksum and
  // before any request, so an oversized file costs the user nothing but a click.
  // Binding rules earlier would change validation timing for every form in the
  // app and is not this guard's business.
  if (ifield.validation.maxBytes) {
    const maxBytes = ifield.validation.maxBytes
    validationArray.push((value: File | File[] | null | undefined) => {
      // `multiple` is per-field and filesChanged() assigns whatever Vuetify
      // hands it, so the value may be a single File or an array of them.
      const files = Array.isArray(value) ? value : value ? [value] : []
      const tooBig = files.find(
        (file) => file instanceof File && file.size > maxBytes
      )
      if (!tooBig) {
        return true
      }
      return (
        `${tooBig.name} is ${formatBytes(tooBig.size)} —` +
        ` the maximum upload size is ${formatBytes(maxBytes)}.`
      )
    })
  }
  // Outside the `if (ifield.value)` gate for the reason maxBytes is: the rule
  // is a function of the spec and the row, not of form state. It no-ops on an
  // empty value; `required` owns emptiness.
  if (ifield.validation.msServiceQuantity) {
    validationArray.push(getMSServiceQuantityValidator(entityData))
  }
  if (ifield.value) {
    if (ifield.validation.minLength) {
      const iv = ifield.validation
      const ivml = iv.minLength
      assertIsNotUndefined(iv)
      assertIsNotUndefined(ivml)
      validationArray.push(
        (value: string) =>
          value.length === 0 ||
          value.length >= ivml ||
          `${ifield.label} must be at least ${ivml} characters long.`
      )
    }
    if (ifield.validation.email) {
      if (isListField) {
        validationArray.push(makeEveryEmailAddressRule(ifield.itemValue))
      } else {
        validationArray.push(...EMAIL_ADDRESS_RULES)
      }
    }
    if (ifield.validation.sameOrGreaterThanISODateStringInIField) {
      const iv = ifield.validation
      const ivfn = iv.sameOrGreaterThanISODateStringInIField
      assertIsNotUndefined(iv)
      assertIsNotUndefined(ivfn)
      const validateAgainstIField = inputFields.find(
        (ifld) => ifld.name === ivfn
      )
      assertIsNotUndefined(validateAgainstIField)
      // Bind the now-defined field to a non-optional local so the pushed rule
      // closure sees the narrowing (assertion narrowing isn't carried into
      // nested closures). The `as any` on the compared value preserves the
      // original loose ISO-date-string comparison.
      const targetIField: IEntityMaskData = validateAgainstIField
      validationArray.push(
        (value: string) =>
          value.length === 0 ||
          value >= (targetIField.value as any) ||
          `${ifield.label} must be >= ${targetIField.label}.`
      )
    }
    if (ifield.validation.kitQuantity) {
      validationArray.push(validateKitQuantity)
    }
    if (ifield.validation.tableHeaderWidth) {
      validationArray.push(validateTableHeaderWidth)
    }
    if (ifield.validation.ipAddress) {
      validationArray.push(validateIpAddress)
    }
    if (ifield.validation.subnetMask) {
      validationArray.push(validateSubnetMask)
    }
    if (ifield.validation.url) {
      validationArray.push(validateUrl)
    }
    if (ifield.validation.labelForFieldName) {
      validationArray.push(validateLabelForFieldName)
    }
  }
  if (ifield.validation.custom) {
    const custom = ifield.validation.custom
    validationArray.push((value: any) =>
      custom(value, { inputFields, entityParentObjectForCreate })
    )
  }
  if (ifield.validation.onePerSection) {
    validationArray.push((value: any) =>
      validateOnePerSection(
        value,
        ifield.items as any[],
        ifield.label || ifield.name
      )
    )
    // ...and that the picks build something: neither the kit nor any Kit
    // taken inside it may come out an empty header.
    validationArray.push((value: any) =>
      validateOptionsBuildContent(value, ifield.items as any[])
    )
  }
  return validationArray
}
